import { useCallback, useEffect, useState } from 'react';
import { GetAdminChangelogEditionAPI, SaveAdminChangelogEditionAPI } from '../../../../api/AdminAPI';

const EMPTY_FORM = {
  name: '', subject: '', preheader: '', intro: '', notify_bell: true,
  video_media_id: null, poster_media_id: null, captions_media_id: null,
};

// EDITION_FULL traz a mídia como objeto ({ id, url }); o formulário guarda só o id
const mediaId = (edition, key) => edition?.[key]?.id ?? edition?.[`${key}_media_id`] ?? null;

const formFromEdition = (edition) => ({
  name: edition.name || '',
  subject: edition.subject || '',
  preheader: edition.preheader || '',
  intro: edition.intro || '',
  notify_bell: edition.notify_bell === undefined ? true : Boolean(Number(edition.notify_bell)),
  video_media_id: mediaId(edition, 'video'),
  poster_media_id: mediaId(edition, 'poster'),
  captions_media_id: mediaId(edition, 'captions'),
});

/** Destaques primeiro, mantendo a ordem dentro de cada grupo (é a ordem do e-mail). */
const highlightsFirst = (list) => [
  ...list.filter((item) => item.edition_highlight),
  ...list.filter((item) => !item.edition_highlight),
];

const itemsFromEdition = (edition) => highlightsFirst((Array.isArray(edition.items) ? edition.items : [])
  .slice()
  .sort((a, b) => (a.position || 0) - (b.position || 0))
  .map((item) => ({ ...item, edition_highlight: Boolean(Number(item.edition_highlight)) })));

/**
 * useEditionEditor
 *
 * Carrega um envio completo (edition.php) e mantém o rascunho da edição na tela: campos,
 * itens na ordem, destaque por envio. save() grava tudo de uma vez (edition_save.php).
 *
 * @param {number|null} editionId - envio aberto no editor (null = fechado)
 */
function useEditionEditor(editionId) {
  const [edition, setEdition] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [items, setItems] = useState([]);
  const [isDirty, setIsDirty] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  const apply = useCallback((full) => {
    setEdition(full);
    setForm(formFromEdition(full));
    setItems(itemsFromEdition(full));
    setIsDirty(false);
  }, []);

  useEffect(() => {
    setEdition(null);
    setError(null);
    if (editionId === null || editionId === undefined) return undefined;
    let active = true;
    setIsLoading(true);
    GetAdminChangelogEditionAPI(editionId, (response) => {
      if (!active) return;
      setIsLoading(false);
      if (!response || response.success === false || !response.edition) {
        setError(response?.error || 'Não foi possível abrir o envio.');
        return;
      }
      apply(response.edition);
    });
    return () => { active = false; };
  }, [editionId, apply]);

  const setField = useCallback((key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setIsDirty(true);
  }, []);

  const changeItems = useCallback((updater) => {
    setItems(updater);
    setIsDirty(true);
  }, []);

  const moveItem = useCallback((index, direction) => changeItems((prev) => {
    const target = index + direction;
    if (target < 0 || target >= prev.length) return prev;
    // Só troca dentro do mesmo grupo: destaques ficam sempre no topo, como no e-mail
    if (prev[index].edition_highlight !== prev[target].edition_highlight) return prev;
    const next = prev.slice();
    [next[index], next[target]] = [next[target], next[index]];
    return next;
  }), [changeItems]);

  // Destaque marcado vai para o fim dos destaques; desmarcado, para o começo dos demais
  const toggleHighlight = useCallback((itemId) => changeItems((prev) => {
    const target = prev.find((item) => item.id === itemId);
    if (!target) return prev;
    const toggled = { ...target, edition_highlight: !target.edition_highlight };
    const rest = prev.filter((item) => item.id !== itemId);
    const highlights = rest.filter((item) => item.edition_highlight);
    const others = rest.filter((item) => !item.edition_highlight);
    return [...highlights, toggled, ...others];
  }), [changeItems]);

  const removeItem = useCallback((itemId) => changeItems((prev) => prev.filter((item) => item.id !== itemId)), [changeItems]);

  // Item novo entra no fim, com o destaque do catálogo como valor inicial
  const addItems = useCallback((newItems) => changeItems((prev) => {
    const existing = new Set(prev.map((item) => item.id));
    const added = newItems
      .filter((item) => !existing.has(item.id))
      .map((item) => ({ ...item, edition_highlight: Boolean(Number(item.highlight)) }));
    return highlightsFirst([...prev, ...added]);
  }), [changeItems]);

  /** Grava o envio; onSaved(edition) recebe o envio atualizado. */
  const save = useCallback((onSaved) => {
    if (!edition) return;
    setIsSaving(true);
    setError(null);
    const body = {
      id: edition.id,
      ...form,
      items: items.map((item, index) => ({ item_id: item.id, position: index + 1, highlight: item.edition_highlight })),
    };
    SaveAdminChangelogEditionAPI(body, (response) => {
      setIsSaving(false);
      if (!response || response.success === false || !response.edition) {
        setError(response?.error || 'Não foi possível salvar o envio.');
        return;
      }
      apply(response.edition);
      if (onSaved) onSaved(response.edition);
    });
  }, [edition, form, items, apply]);

  /** Grava só se houver mudança; depois chama next(edition). */
  const saveIfDirty = useCallback((next) => {
    if (isDirty) save(next);
    else if (edition) next(edition);
  }, [isDirty, save, edition]);

  const blockedCount = items.filter((item) => item.awaiting_deploy).length;

  return {
    edition, form, items, isDirty, isLoading, isSaving, error, blockedCount,
    setField, moveItem, toggleHighlight, removeItem, addItems, save, saveIfDirty,
    clearError: () => setError(null),
  };
}

export default useEditionEditor;
